"use client";

import type * as React from "react";
import { useEffect, useRef } from "react";
import type { RentalChargesBreakdown, RentalChargeLineItem } from "@/lib/types/booking-rental";
import { formatAud } from "@/lib/vehicles";
import { LucideLoaderCircle, LucideX } from "lucide-react";

type BookingPriceDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  breakdown: RentalChargesBreakdown | null;
  isLoading: boolean;
  error: string | null;
};

export const BookingPriceDetailsModal: React.FC<BookingPriceDetailsModalProps> = ({
  open,
  onClose,
  breakdown,
  isLoading,
  error,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const lineItems = breakdown ? getBreakdownLineItems(breakdown) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close price details"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="price-details-title"
        className="relative z-10 w-full max-w-md rounded-2xl bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 id="price-details-title" className="text-lg font-semibold text-gray-900">
            Price details
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex size-9 cursor-pointer items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <LucideX size={20} />
          </button>
        </div>

        <div className="px-6 py-5">
          {isLoading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <LucideLoaderCircle className="animate-spin text-blue-300" size={32} />
              <p className="text-sm text-gray-600">Loading price details...</p>
            </div>
          ) : error ? (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
          ) : breakdown ? (
            <ul className="space-y-4">
              {lineItems.map((item) => (
                <li
                  key={item.key}
                  className="flex items-start justify-between gap-4 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    {item.subtitle ? (
                      <p className="mt-0.5 text-gray-500">{item.subtitle}</p>
                    ) : null}
                  </div>
                  <span className="shrink-0 font-medium text-gray-900">
                    ${formatAud(item.amount)}
                  </span>
                </li>
              ))}

              {breakdown.discount.amount !== 0 ? (
                <li className="flex items-start justify-between gap-4 border-t border-gray-200 pt-4 text-sm">
                  <p className="font-medium text-green-700">{breakdown.discount.name}</p>
                  <span className="shrink-0 font-medium text-green-700">
                    -${formatAud(Math.abs(breakdown.discount.amount))}
                  </span>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>

        {breakdown && !isLoading && !error ? (
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-gray-900">
                {breakdown.total.name}
              </span>
              <span className="text-xl font-bold text-gray-900">
                ${formatAud(breakdown.total.amount)}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

type BreakdownDisplayItem = {
  key: string;
  name: string;
  subtitle?: string;
  amount: number;
};

function getBreakdownLineItems(
  breakdown: RentalChargesBreakdown,
): BreakdownDisplayItem[] {
  const items: BreakdownDisplayItem[] = [
    toDisplayItem(breakdown.rental_days),
    toDisplayItem(breakdown.package),
    ...breakdown.miscellaniouse.map(toDisplayItem),
  ];

  return items;
}

function toDisplayItem(item: RentalChargeLineItem): BreakdownDisplayItem {
  return {
    key: item.key,
    name: item.name,
    subtitle: formatQuantityRate(item.quantity, item.rate),
    amount: item.amount,
  };
}

function formatQuantityRate(
  quantity?: number,
  rate?: number,
): string | undefined {
  if (quantity === undefined || rate === undefined) {
    return undefined;
  }

  return `${quantity} x $${formatAud(rate)}`;
}
